import React from "react";
import { APPS, FROSTED_PANEL_STYLE } from "./constants";
import { GET_IMAGE } from "../../helpers/images";
import { GMB_LOGO_URL } from "../../constants/branding";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";

/** Map operand from kpi.optional.operand (add, subtract, multiply, divide) to display symbol */
const OPERATOR_SYMBOLS = {
  add: "+",
  subtract: "−",
  multiply: "×",
  divide: "÷",
};

/**
 * Resolve metric name by KPI id from allMetrics or rawKpis.
 * Composite data comes from kpi.optional.sourceId1, kpi.optional.operand, kpi.optional.sourceId2.
 */
function getMetricNameById(id, allMetrics = [], rawKpis = []) {
  if (!id) return "";
  const fromMetric = allMetrics.find((m) => m.id === id || m.dashboardId === id || (m._source === "companyKpi" && (m.id || m._id) === id));
  if (fromMetric?.name) return fromMetric.name;
  const fromKpi = rawKpis.find((k) => (k._id || k.id) === id);
  return fromKpi?.title || fromKpi?.name || id;
}

function getLogoFromIntegrationApps(integrationApps, source) {
  if (!Array.isArray(integrationApps) || !source) return null;
  const app = integrationApps.find((a) => (a.code || a.id) === source);
  return app?.logo ? GET_IMAGE(app.logo) || app.logo : null;
}

/**
 * Metric details modal — read-only summary of a metric's source, composite
 * make-up and reset behaviour. Uses the shared modal chrome: icon + title +
 * X close in the header, tight 20px padding, a single primary action grouped
 * bottom-right. Nothing here warrants a tall modal, so the layout stays compact.
 */
function MetricDetailsModal({
  isOpen,
  onClose,
  metric,
  allMetrics = [],
  rawKpi = null,
  rawKpis = [],
  integrationApps = [],
}) {
  useLockBodyScroll(isOpen);

  if (!isOpen) return null;

  const gameConfig = metric?.gameConfig || {};
  const opt = rawKpi?.optional || {};
  const hasComposite = !!(opt.sourceId1 && opt.sourceId2);
  const operand = opt.operand || "add";

  const sourceApp = APPS.find((a) => a.id === metric?.appId);
  const appIconFromIntegration = integrationApps?.length && metric?.appId
    ? getLogoFromIntegrationApps(integrationApps, metric.appId)
    : null;
  const appIcon = appIconFromIntegration || sourceApp?.icon || GMB_LOGO_URL;
  const appName = sourceApp?.name || metric?.appId || "Metric";

  const getMetricName = (id) => getMetricNameById(id, allMetrics, rawKpis);
  const operatorSymbol = OPERATOR_SYMBOLS[operand] || operand;

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="rounded-lg w-full max-w-[440px] flex flex-col overflow-hidden max-h-[85vh]"
        style={FROSTED_PANEL_STYLE}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — icon + title, X close top-right */}
        <header className="flex items-start justify-between px-[20px] py-[16px] border-b border-[rgba(15,23,42,0.06)] flex-shrink-0">
          <div className="flex items-start gap-[11px] min-w-0">
            <div className="w-[34px] h-[34px] rounded-lg flex-shrink-0 flex items-center justify-center bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-[17px] h-[17px]">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
            </div>
            <h3 className="text-[15px] font-semibold text-gray-900 leading-[1.25] pt-[6px]">Metric details</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close metric details"
            className="w-[30px] h-[30px] rounded-[7px] flex-shrink-0 flex items-center justify-center bg-[rgba(15,23,42,0.04)] text-gray-400 hover:bg-[rgba(15,23,42,0.07)] hover:text-gray-600 transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[15px] h-[15px]" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Body */}
        <div className="px-[20px] py-[16px] overflow-y-auto">
          {/* Connected data source */}
          <div className="mb-[14px]">
            <h4 className="text-[11px] font-medium text-gray-400 mb-[6px]">Connected data source</h4>
            {(sourceApp || appIcon) ? (
              <div className="flex items-center gap-[12px] p-[12px] bg-[#f8fafc] rounded-[10px] border border-[#eef0f2]">
                <div className="w-[34px] h-[34px] rounded-lg bg-white border border-[#eef0f2] p-[6px] flex items-center justify-center flex-shrink-0">
                  <img src={appIcon} alt={appName} className="w-full h-full object-contain" />
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-medium text-gray-900 capitalize truncate">{appName}</p>
                  <p className="text-[11.5px] text-gray-400 font-medium truncate">{metric?.name || rawKpi?.title || rawKpi?.name || "—"}</p>
                </div>
              </div>
            ) : (
              <div className="p-[12px] bg-[#f8fafc] rounded-[10px] border border-dashed border-[#dde2e8]">
                <p className="text-[12.5px] font-medium text-gray-400">No connected data source.</p>
              </div>
            )}
          </div>

          {/* Composite structure from kpi.optional.sourceId1, operand, sourceId2 */}
          <div>
            <h4 className="text-[11px] font-medium text-gray-400 mb-[6px]">Composite structure</h4>
            {hasComposite ? (
              <div className="p-[12px] bg-[var(--gmb-primary-soft)] rounded-[10px] border border-[var(--gmb-primary-tint)]">
                <div className="flex items-center flex-wrap gap-[8px] text-[13px] font-medium text-gray-900">
                  <span className="px-[10px] py-[6px] bg-white rounded-lg border border-[var(--gmb-primary-tint)] shadow-sm">
                    {getMetricName(opt.sourceId1)}
                  </span>
                  <span className="w-[26px] h-[26px] flex items-center justify-center text-[var(--gmb-primary)] font-medium text-base">
                    {operatorSymbol}
                  </span>
                  <span className="px-[10px] py-[6px] bg-white rounded-lg border border-[var(--gmb-primary-tint)] shadow-sm">
                    {getMetricName(opt.sourceId2)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="p-[12px] bg-[#f8fafc] rounded-[10px] border border-dashed border-[#dde2e8]">
                <p className="text-[12.5px] font-medium text-gray-400">This metric is not composite.</p>
              </div>
            )}
          </div>

          {/* Value type / reset frequency */}
          <div className="grid grid-cols-2 gap-[14px] pt-[14px] mt-[14px] border-t border-[#eef0f2]">
            <div>
              <p className="text-[11px] font-medium text-gray-400 mb-[3px]">Value type</p>
              <p className="text-[13px] font-medium text-gray-900 capitalize">
                {metric?.unit === "currency" ? "Monetary" : "Unit"}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-gray-400 mb-[3px]">Reset frequency</p>
              <p className="text-[13px] font-medium text-gray-900 capitalize">
                {gameConfig?.resetFrequency || "Daily"}
              </p>
            </div>
            {gameConfig?.monitorMode && (
              <div className="col-span-2">
                <div className="inline-flex items-center gap-[6px] px-[10px] py-[6px] rounded-lg bg-amber-50 text-amber-600 border border-amber-100">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[13px] h-[13px]">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <span className="text-[12px] font-medium">Monitor mode enabled</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer — single primary, grouped bottom-right */}
        <footer className="flex items-center justify-end gap-[10px] px-[20px] py-[14px] border-t border-[rgba(15,23,42,0.06)] bg-[rgba(248,250,252,0.5)] flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-[24px] py-[8px] bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-[13px] shadow-sm hover:brightness-95 transition-all"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

export default MetricDetailsModal;
